import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      setSelectedSize(response.data.sizes[0]);
      setSelectedColor(response.data.colors[0]);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  const getColorValue = (colorName) => {
    const colors = {
      'Wheat': '#D4A574',
      'Black': '#000000',
      'Brown': '#8B4513',
      'Cherry Red': '#C41E3A',
      'White': '#FFFFFF',
      'Tan': '#D2B48C',
      'Navy': '#000080',
      'Walnut': '#773F1A'
    };
    return colors[colorName] || '#CCCCCC';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-indicator">
        <p className="text-[#5D5C56]">Loading product...</p>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="min-h-screen py-24 px-6 md:px-12 lg:px-24" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
          <div className="space-y-4">
            <div className="aspect-square bg-[#F5F5F0] overflow-hidden relative border-4 border-gray-200 rounded-lg" data-testid="product-image-main">
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              {/* Color Overlay Effect */}
              <div 
                className="absolute inset-0 transition-all duration-500 ease-in-out pointer-events-none"
                style={{ 
                  backgroundColor: getColorValue(selectedColor),
                  mixBlendMode: 'multiply',
                  opacity: 0.35
                }}
              ></div>
              {/* Selected Color Badge */}
              <div className="absolute top-4 left-4 bg-white px-4 py-2 rounded-full shadow-lg border-2 border-[#14B8A6] z-10">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-6 h-6 rounded-full border-2 border-gray-300" 
                    style={{ backgroundColor: getColorValue(selectedColor) }}
                  ></div>
                  <span className="text-sm font-semibold text-gray-900">{selectedColor}</span>
                </div>
              </div>
              {/* Color Selection Note */}
              <div className="absolute bottom-4 left-4 right-4 bg-[#14B8A6]/90 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-center z-10">
                <p className="text-sm font-medium">Color Preview: {selectedColor} • Size {selectedSize}</p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <p className="text-sm uppercase tracking-wider text-[#14B8A6] mb-2 font-semibold" data-testid="product-brand">
                {product.brand}
              </p>
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-gray-900 mb-4" data-testid="product-name">
                {product.name}
              </h1>
              <div className="inline-block px-4 py-2 bg-[#14B8A6]/10 rounded-lg border border-[#14B8A6]">
                <p className="text-base font-semibold text-[#14B8A6]">Contact us for pricing information</p>
              </div>
            </div>

            <p className="text-base text-[#5D5C56] leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            <div className="border-t border-[#E5E4DE] pt-6">
              <label className="block text-sm font-semibold text-[#1F2937] mb-3" data-testid="size-label">
                Select Size
              </label>
              <div className="flex flex-wrap gap-3" data-testid="size-selector">
                {product.sizes.map(size => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`size-button ${selectedSize === size ? 'selected' : ''}`}
                    data-testid={`size-option-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6 bg-gray-50 -mx-6 px-6 py-6">
              <label className="block text-base font-bold text-gray-900 mb-4" data-testid="color-label">
                Choose Color
              </label>
              <div className="mb-4 p-3 bg-white rounded-lg border-2 border-[#14B8A6]">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full border-3 border-gray-300 shadow-md" 
                    style={{ backgroundColor: getColorValue(selectedColor) }}
                  ></div>
                  <div>
                    <p className="text-sm text-gray-500">Selected Color</p>
                    <p className="text-lg font-bold text-gray-900">{selectedColor}</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-3" data-testid="color-selector">
                {product.colors.map(color => (
                  <button
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={`color-swatch ${selectedColor === color ? 'selected' : ''} relative`}
                    style={{ backgroundColor: getColorValue(color) }}
                    title={color}
                    data-testid={`color-option-${color}`}
                  >
                    {selectedColor === color && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#14B8A6] rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                    <span className="sr-only">{color}</span>
                  </button>
                ))}
              </div>
            </div>

            <Link to="/contact" className="block">
              <button
                className="btn-primary w-full flex items-center justify-center"
                data-testid="contact-us-button"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Contact Us for Availability
              </button>
            </Link>

            <div className="bg-gray-50 p-6 space-y-2" data-testid="product-info">
              <p className="text-sm text-[#5D5C56]">
                <strong>Category:</strong> {product.category === 'boots' ? 'Boots' : "Men's Shoes"}
              </p>
              <p className="text-sm text-[#5D5C56]">
                <strong>In Stock:</strong> Yes
              </p>
              <p className="text-sm text-[#5D5C56]">
                <strong>Brand:</strong> {product.brand}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;